import React, { useContext, useEffect, useState } from 'react';
import { Collapse, Form } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';

import { formatterPhone as utilsFormatterPhone } from '../../../../utils/formatters/formatter';
import { TaskContext } from '../Insurance';
import ModalFinishTask from './ModalFinishTask';
import { useGetTaskResponsesByTask } from '../../../../services/task/querys';
import { Task, TaskResponse, TaskState } from '../../../../gql/graphql';
import { useCreateResponseTask } from '../../../../services/task/mutations';
import SectionIssues from '../../../SectionIssues/SectionIssues';
import InputBorderNone from '../../components/Atoms/Inputs/InputBorderNone/InputBorderNone';
import ButtonSubmit from '../../components/Atoms/Buttons/ButtonSubmit/ButtonSubmit';
import UploadDocs from '../../components/Atoms/UploadDocs/UploadDocs';
import ActionAcceptAndReject from '../../../ActionAcceptAndReject/ActionAcceptAndReject';
import StateTag from '../../components/Atoms/Tags/StateTag/StateTag';
import CardBorrowerImpersonation from '../../../CardBorrowerImpersonation/CardBorrowerImpersonation';

interface IForm {
  titleCompany: string;
  name: string;
  phone: string;
  email: string;
}

interface Props {
  setStep: Function;
  closeTask: Function;
}

const Step2 = ({ setStep, closeTask }: Props) => {
  const [form] = Form.useForm();
  const task: Task | any = useContext(TaskContext);

  const { data: taskResponses, reexecute: getTaskResponses } = useGetTaskResponsesByTask({
    variables: { findResponsesByTask: { taskId: task._id } }
  });
  const { fetching, execute: createResponseTask } = useCreateResponseTask();

  const [listFileUpload, setListFileUpload] = useState([]);
  const [modalFinishTask, setModalFinishTask] = useState<boolean>(false);
  const [responseInfo, setResponseInfo] = useState<TaskResponse | any>();
  const [responseDoc, setResponseDoc] = useState<TaskResponse | any>();
  const [taskCompleted, setTaskCompleted] = useState<boolean>();

  useEffect(() => {
    if (responseInfo)
      form.setFieldsValue(responseInfo.metadata);
  }, [responseInfo])

  useEffect(() => {
    const _response = taskResponses?.find((item) => item.step === 2 && item.key === 'insurance-information');
    setResponseInfo(_response);

    const _response2 = taskResponses?.find((item) => item.step === 2 && item.key === 'insurance-doc');
    setResponseDoc(_response2);

    setTaskCompleted(_response?.state === TaskState.Approved && _response2?.state === TaskState.Approved);
  }, [taskResponses]);

  const formatterPhone = (e: any) => {
    form.setFieldValue('phone', utilsFormatterPhone(e.target.value));
  };

  const onFinish = async (values: IForm) => {
    await createResponseTask({
      task: task._id,
      key: `insurance-information`,
      label: `We’ll reach out and get insurance documents`,
      step: 2,
      metadata: values,
      state: TaskState.Review
    });

    getTaskResponses({ requestPolicy: 'network-only' });

    if (responseDoc)
      setModalFinishTask(true);
  }

  const onSubmitDoc = async () => {
    await createResponseTask({
      task: task._id,
      key: `insurance-doc`,
      label: `I want to upload my own insurance docs`,
      step: 2,
      metadata: {},
      state: TaskState.Review,
      files: listFileUpload
    });

    getTaskResponses({ requestPolicy: 'network-only' });

    if (responseInfo)
      setModalFinishTask(true);
  }

  return (
    <section className='insurance-step-2 '>
      <div className="section-1">
        <h3 className="h3">
          Who is your current insurance agent?
        </h3>
        <div className="mt-10">
          <p className="h4 h4-w4">
            We’ll reach out and get insurance documents
          </p>
        </div>

        <CardBorrowerImpersonation hasResponses={taskResponses && taskResponses.length > 0} />
        <SectionIssues task={task} />

        <div
          style={{ position: 'relative' }}
          className={`mt-40 title-company-step-1 title-company-step-1-${responseInfo?.state}`}
        >
          {responseInfo &&
            <ActionAcceptAndReject
              taskResponseId={responseInfo?._id}
              task={{ id: task._id, key: task.key, name: task.name }}
              files={responseInfo?.files}
              currentState={responseInfo?.state}
            />
          }

          {responseInfo?.state && responseInfo?.state !== TaskState.Review && <StateTag state={responseInfo?.state} />}
          {responseInfo?.state && responseInfo?.state === TaskState.Review && <StateTag state={responseInfo?.state} isLabelLocked />}

          <Form
            name="edit"
            autoComplete="off"
            requiredMark="optional"
            colon={false}
            form={form}
            onFinish={onFinish}
          >
            <Form.Item
              label=""
              name="insuranceCompany"
              className="titleCompany"
              rules={[{ required: true, message: '' }]}
            >
              <InputBorderNone
                placeholder="Insurance company name"
                className="bold"
              />
            </Form.Item>
            <Form.Item
              label="Contact name (if different from lender)"
              name="name"
              className="name"
              rules={[{ required: true, message: '' }]}
            >
              <InputBorderNone
                placeholder="Edgar"
                height={20}
                className="bold-lith"
              />
            </Form.Item>

            <Form.Item
              label="Phone number"
              name="phone"
              className="phone"
              rules={[
                { required: true, message: '' },
                { min: 12, message: '' }
              ]}
            >
              <InputBorderNone
                placeholder="329-230-2390"
                height={20}
                className="bold-lith"
                onChange={formatterPhone}
              />
            </Form.Item>

            <Form.Item
              label="Email address"
              name="email"
              className="email"
              rules={[{ required: true, type: 'email', message: '' }]}
            >
              <InputBorderNone
                type="email"
                placeholder="ricardo@mervetscoolbiz.com"
                height={20}
                className="bold-lith"
              />
            </Form.Item>

            {responseInfo?.state !== TaskState.Approved &&
              <Form.Item className="submit" shouldUpdate>
                {() => (
                  <ButtonSubmit
                    disabled={
                      !!responseInfo ||
                      !form.getFieldsError().every((fieldErr) => fieldErr.errors.length === 0) ||
                      !form.isFieldsTouched() ||
                      !Object.values(form.getFieldsValue()).every(value => value)
                    }
                    loading={fetching}
                    modalConfirm={{ show: true }}
                    onClick={form.submit}
                  >
                    Submit for review
                  </ButtonSubmit>
                )}
              </Form.Item>
            }
          </Form>
        </div>
        <div className='mt-10'>
          <Collapse
            ghost
            expandIconPosition="right"
            expandIcon={(collapse) => !collapse.isActive ? <DownOutlined /> : <UpOutlined />}
            items={[
              {
                key: '1',
                label: 'I WANT TO UPLOAD MY OWN INSURANCE DOCS',
                children:
                  <UploadDocs
                    numberOfFiles={1}
                    listFileUpload={responseDoc?.files || listFileUpload}
                    setListFileUpload={setListFileUpload}
                    button={
                      <ButtonSubmit
                        disabled={listFileUpload?.length === 0 || !!responseDoc}
                        loading={fetching}
                        modalConfirm={{ show: true }}
                        onClick={onSubmitDoc}
                      >
                        Upload docs to submit
                      </ButtonSubmit>
                    }
                    insertHtml={
                      <ActionAcceptAndReject
                        taskResponseId={responseDoc?._id}
                        task={{ id: task._id, key: task.key, name: task.name }}
                        files={responseDoc?.files}
                        currentState={responseDoc?.state}
                        rightStyle={40}
                      />
                    }
                  />,
              }]} />
        </div>
      </div>

      <ModalFinishTask
        visible={modalFinishTask}
        setVisible={setModalFinishTask}
        closeTask={closeTask}
      />
    </section>
  );
};
export default Step2;
